/**
 * The lab's news as a small carousel in the side column of the lab page: on each slide a small avatar
 * of the person the news is about (or the lab's initials), then the headline, the date and person, and the
 * short text. No frame: it sits directly in the News section.
 * News carry no pictures. Arrows, dots and the keyboard move between slides; a gentle auto-advance
 * pauses on hover / focus and never runs for people who prefer reduced motion.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, IconButton, Link, Typography } from '@mui/material';
import {
  ChevronLeft as PrevIcon, ChevronRight as NextIcon, OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

const ADVANCE_MS = 8000;
const AVATAR = 40;

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

export default function LabNewsCarousel({ news, lab, color, initials, avatarSx, controls }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = news.length;
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => { if (index >= count) setIndex(0); }, [count, index]);
  const go = useCallback((step) => setIndex((i) => (i + step + count) % count), [count]);
  useEffect(() => {
    if (count < 2 || paused || reduceMotion) return undefined;
    const t = setInterval(() => go(1), ADVANCE_MS);
    return () => clearInterval(t);
  }, [count, paused, reduceMotion, go]);

  if (!count) return null;
  const n = news[Math.min(index, count - 1)];
  const who = n.person_detail;

  return (
    <Box
      role="region" aria-roledescription="carousel" aria-label={`${lab.name} news`} tabIndex={0}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
      onKeyDown={(e) => { if (e.key === 'ArrowLeft') go(-1); if (e.key === 'ArrowRight') go(1); }}
      sx={{ borderRadius: 2, bgcolor: 'background.paper',
        outline: 'none', '&:focus-visible': { boxShadow: `0 0 0 2px ${color}` } }}
    >
      <Box key={n.id} aria-roledescription="slide" aria-label={`${index + 1} of ${count}`}
        sx={{ display: 'flex', gap: 1.25, px: 0.5, py: 0.5, minHeight: 110,
          '@keyframes newsIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          animation: reduceMotion ? 'none' : 'newsIn 450ms ease' }}>
        {who ? (
          <Avatar src={who.avatar_url || undefined} alt={who.name}
            sx={{ width: AVATAR, height: AVATAR, fontSize: 16, flexShrink: 0, ...avatarSx }}>
            {(who.name || '?')[0]}
          </Avatar>
        ) : (
          <Avatar variant="rounded" aria-label={lab.name}
            sx={{ width: AVATAR, height: AVATAR, fontSize: 14, fontWeight: 700, flexShrink: 0, bgcolor: color, color: '#fff' }}>
            {initials}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Typography sx={{ fontSize: 16.5, fontWeight: 700, lineHeight: 1.3, flex: 1, minWidth: 0 }}>
              {n.link ? (
                <Link href={n.link} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">
                  {n.title} <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                </Link>
              ) : n.title}
            </Typography>
            {controls?.(n)}
          </Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.4, mt: 0.25 }} noWrap>
            {fmtDate(n.date)}{who ? ` · ${who.name}` : ''}
          </Typography>
          {n.body && (
            <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: 'text.secondary', mt: 0.5, whiteSpace: 'pre-line',
              display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {n.body}
            </Typography>
          )}
        </Box>
      </Box>

      {count > 1 && (
        <Box display="flex" alignItems="center" justifyContent="space-between"
          sx={{ pt: 0.25 }}>
          <IconButton aria-label="Previous news" size="small" onClick={() => go(-1)}><PrevIcon fontSize="small" /></IconButton>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {news.map((item, i) => (
              <Box key={item.id} component="button" type="button" aria-label={`News ${i + 1}`} aria-current={i === index}
                onClick={() => setIndex(i)}
                sx={{ width: i === index ? 18 : 7, height: 7, p: 0, border: 0, borderRadius: 4, cursor: 'pointer',
                  bgcolor: i === index ? color : 'action.disabled', transition: 'width 200ms ease' }} />
            ))}
          </Box>
          <IconButton aria-label="Next news" size="small" onClick={() => go(1)}><NextIcon fontSize="small" /></IconButton>
        </Box>
      )}
    </Box>
  );
}
